import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';
import { AudioSource as Source } from 'features/call/call-base/enums';
import { isMicrophoneMutedSelector } from 'features/call/call-base/store';
import {
  isDeafConnectedSelector,
  isDeafMutedSelector,
} from 'features/call/call-deaf/store';
import { selectIsAnyHearingConnectedOrConnecting } from 'features/call/call-hearing/store';
import { isRemoteTeamingStartedSelector } from 'features/teaming/teaming-base/store';
import {
  isPrimaryVcoSenderSelector,
  isVcoRequestedSelector,
} from 'features/vco/store';

// Lesson learned: selectors need to be in a separate file from their slice.
// Want to know why? Me, too.

// Inputs are arrow-wrapped so the referenced selectors resolve at call time, which
// keeps the file free of module-load cycles while giving Reselect stable primitive
// inputs (so the result function only re-runs when an audio-affecting boolean flips).

/** Returns the audio sources that should be sent to the hearing line. */
export const sourcesForHearingLineSelector = createSelector(
  [
    (s: RootState) => s.audio.isAudioReady,
    (s: RootState) => isVcoRequestedSelector(s),
    (s: RootState) => isRemoteTeamingStartedSelector(s),
    (s: RootState) => selectIsAnyHearingConnectedOrConnecting(s),
    (s: RootState) => isPrimaryVcoSenderSelector(s),
    (s: RootState) => isDeafConnectedSelector(s),
    (s: RootState) => isDeafMutedSelector(s),
    (s: RootState) => isMicrophoneMutedSelector(s),
  ],
  (
    isAudioReady,
    is1LineVco,
    isTeaming,
    isHearingConnectedOrConnecting,
    isPrimaryVcoSender,
    isDeafConnected,
    isDeafMuted,
    isMuted
  ) => {
    if (!isAudioReady) {
      return [];
    }

    const sources: Source[] = [];
    const isAnyOtherHearing = isTeaming || isHearingConnectedOrConnecting;

    if (isAnyOtherHearing && !isMuted) {
      sources.push(Source.InputDevice);
    }

    if (
      isAnyOtherHearing &&
      is1LineVco &&
      isPrimaryVcoSender &&
      isDeafConnected &&
      !isDeafMuted
    ) {
      sources.push(Source.DirectLine);
    }

    return sources;
  }
);

/** Returns the audio sources that should be sent to the direct line. */
export const sourcesForDirectLineSelector = createSelector(
  [
    (s: RootState) => s.audio.isAudioReady,
    (s: RootState) => isVcoRequestedSelector(s),
    (s: RootState) => selectIsAnyHearingConnectedOrConnecting(s),
    (s: RootState) => isRemoteTeamingStartedSelector(s),
    (s: RootState) => isPrimaryVcoSenderSelector(s),
    (s: RootState) => isMicrophoneMutedSelector(s),
  ],
  (
    isAudioReady,
    is1LineVco,
    isHearingConnectedOrConnecting,
    isTeamConnected,
    isPrimaryVcoSender,
    isMuted
  ) => {
    if (!isAudioReady) {
      return [];
    }

    const sources: Source[] = [];

    if (!isMuted && is1LineVco && isPrimaryVcoSender) {
      sources.push(Source.InputDevice);
    }

    if (
      isPrimaryVcoSender &&
      is1LineVco &&
      (isHearingConnectedOrConnecting || isTeamConnected)
    ) {
      sources.push(Source.VoiceLine);
    }

    return sources;
  }
);

/** Returns the audio sources that should be audible (played on the speaker/headset). */
export const audibleSourcesSelector = createSelector(
  [
    (s: RootState) => s.audio.isAudioReady,
    (s: RootState) => isVcoRequestedSelector(s),
    (s: RootState) => isPrimaryVcoSenderSelector(s),
    (s: RootState) => isDeafMutedSelector(s),
    (s: RootState) => isRemoteTeamingStartedSelector(s),
    (s: RootState) => selectIsAnyHearingConnectedOrConnecting(s),
  ],
  (
    isAudioReady,
    is1LineVco,
    isPrimaryVcoSender,
    isDeafMuted,
    isTeaming,
    isHearingConnected
  ) => {
    if (!isAudioReady) {
      return [];
    }

    const sources: Source[] = [];

    if (is1LineVco && isPrimaryVcoSender && !isDeafMuted) {
      sources.push(Source.DirectLine);
    }

    if (isTeaming || isHearingConnected) {
      sources.push(Source.VoiceLine);
    }

    return sources;
  }
);

export const audioFlowSelector = createSelector(
  sourcesForDirectLineSelector,
  sourcesForHearingLineSelector,
  audibleSourcesSelector,
  (toDirectLine, toVoiceLine, audible) => ({
    toDirectLine,
    toVoiceLine,
    audible,
  })
);

export const isAudioReadySelector = (state: RootState) => {
  return state.audio.isAudioReady;
};
